import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { RouteComponentProps, withRouter } from 'react-router'

import MobileView from '~/client/src/mobile/components/MobileView'
import MobileCommonStore from '~/client/src/mobile/stores/ui/MobileCommon.store'
import WorkflowViewFormWrapper from '~/client/src/shared/components/SitePermitCreationForm/WorkflowViewFormWrapper'
import useWorkflowsByIds from '~/client/src/shared/hooks/useWorkflowsByIds'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import { notifyOverlayOpen } from '~/client/src/shared/utils/overlayNotifier'

import { Loader } from '../../shared/components/Loader'
import Workflow from '../../shared/models/Workflow'
import FormCategoriesStore from '../../shared/stores/domain/FormCategories.store'
import PermitTypesStore from '../../shared/stores/domain/PermitTypes.store'
import MobileFileInput from '../components/FileInput/MobileFileInput'
import MobileEventStore from '../stores/EventStore/MobileEvents.store'

interface IProps {
  common?: MobileCommonStore
  eventsStore?: MobileEventStore
  permitTypesStore?: PermitTypesStore
  companiesStore?: CompaniesStore
  formCategoriesStore?: FormCategoriesStore
  workflow?: Workflow
  workflowLoading?: boolean
}

interface IRouteParams {
  formId: string
}

@inject('common', 'permitTypesStore', 'companiesStore', 'formCategoriesStore')
@observer
class PermitViewComponent extends React.Component<
  IProps & RouteComponentProps<IRouteParams>
> {
  @observable private localProps: IProps & RouteComponentProps<IRouteParams>
  private closeOverlay: (() => void) | null = null

  public constructor(props: IProps & RouteComponentProps<IRouteParams>) {
    super(props)

    this.localProps = props

    makeObservable(this)
  }

  public componentDidMount() {
    const { common } = this.props
    common.hideNavBar()
    this.closeOverlay = notifyOverlayOpen()
  }

  public componentWillUnmount() {
    this.closeOverlay?.()
    this.closeOverlay = null
  }

  public componentDidUpdate(
    prevProps: Readonly<IProps & RouteComponentProps>,
  ): void {
    if (this.props !== prevProps) {
      this.localProps = this.props
    }
  }

  public render() {
    if (this.isLoading) {
      return <Loader />
    }

    const { workflow } = this.localProps

    if (!workflow) {
      return (
        <MobileView
          content={
            <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-full tw:gap-4 tw:p-6 tw:text-center">
              <p className="tw:text-lg tw:text-foreground">
                {Localization.translator.workflowDeleted}
              </p>
              <button
                className="tw:text-primary tw:underline"
                onClick={this.goBack}
              >
                {Localization.translator.goBack}
              </button>
            </div>
          }
          footer={null}
          header={null}
          imagePreview={null}
        />
      )
    }

    return (
      <MobileView
        content={
          <WorkflowViewFormWrapper
            key={workflow.id}
            workflowToShow={workflow}
            onClose={this.goBack}
            FileInputType={MobileFileInput}
            isMobileMode={true}
          />
        }
        footer={null}
        header={null}
        imagePreview={null}
      />
    )
  }

  @action.bound
  private goBack() {
    const { common } = this.props
    common.toggleNavBar()
    common.history.goBack()
  }

  @computed
  private get isLoading(): boolean {
    const {
      permitTypesStore,
      companiesStore,
      formCategoriesStore,
      workflowLoading,
    } = this.localProps
    return (
      workflowLoading ||
      !permitTypesStore.isDataReceived ||
      !companiesStore.isDataReceived ||
      !formCategoriesStore.isDataReceived
    )
  }
}

const PermitView = inject('eventsStore')(
  observer((props: IProps & RouteComponentProps<IRouteParams>) => {
    const { eventsStore, match } = props
    const { appState } = eventsStore

    const { workflows, workflowLoading } = useWorkflowsByIds(
      appState.activeProject.id,
      [match.params.formId],
      false,
      true,
    )

    return (
      <PermitViewComponent
        {...props}
        workflow={workflows?.[0]}
        workflowLoading={workflowLoading}
      />
    )
  }),
)

export default withRouter(PermitView)
